import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { DefaultSortTypesEnum, Pagination } from '@/common/types';
import { API_URL, IContributionActivityRecordResponseDto } from '@/dtos';

import { TaxType } from '../../Contributions/contributions.types';
import { ContributionActivity, ContributionActivityStatus } from '../types';

export const GET_CONTRIBUTION_ACTIVITIES_QUERY_KEY = 'useGetContributionActivitiesQuery';
const SORT_KEY = 'recordDetails.employeeFullName';
const VALIDATION_SORT_KEY = `isValid`;
const getSorting = (validationSortBy?: DefaultSortTypesEnum, sortBy?: DefaultSortTypesEnum) => {
  if (!validationSortBy || !sortBy) return '';
  return (
    '?sort=' +
    [
      validationSortBy ? `${VALIDATION_SORT_KEY},${validationSortBy}` : '',
      sortBy ? `${SORT_KEY},${sortBy}` : '',
    ]
      .filter(Boolean)
      .join('&sort=')
  );
};


interface Props {
  contributionId?: string;
  page?: number;
  size?: number;
  statuses?: ContributionActivityStatus[];
  taxYears?: string[];
  sortBy?: DefaultSortTypesEnum;
  validationSortBy?: DefaultSortTypesEnum;
  planCodes?: string[];
  employeeName?: string;
}

const useGetContributionActivitiesQuery = ({
  contributionId,
  statuses,
  page,
  size,
  sortBy,
  validationSortBy,
  taxYears,
  planCodes,
  employeeName,
}: Props) => {
  const { data, isLoading } = useQuery(
    [
      GET_CONTRIBUTION_ACTIVITIES_QUERY_KEY,
      contributionId,
      statuses,
      page,
      size,
      sortBy,
      validationSortBy,
      taxYears,
      planCodes,
      employeeName,
    ],
    () =>
      api.get<Pagination<IContributionActivityRecordResponseDto>>(
        `${API_URL.CONTRIBUTIONS_CONTRIBUTION_REQUESTS_ACTIVITY}${getSorting(validationSortBy, sortBy)}`,
        {
          params: {
            ...(contributionId ? { contribution_id: contributionId } : {}),
            ...(statuses?.length ? { statuses: statuses.join(',') } : {}),
            ...(taxYears?.length ? { tax_years: taxYears.join(',') } : {}),
            ...(page && page > 1 ? { page: page - 1 } : {}),
            ...(planCodes?.length ? { plan_codes: planCodes.join(',') } : {}),
            ...(employeeName ? { employee_name: employeeName } : {}),
            size,
          },
        }
      ),
    {
      enabled: Boolean(contributionId),
    }
  );

  const formattedData = useMemo(
    () =>
      (data?.data?.content || [])?.map(
        (activity): ContributionActivity => ({
          recordId: toString(activity?.record_id),
          processingStatus: activity?.processing_status as unknown as ContributionActivityStatus,
          planCode: activity?.plan_code,
          // TODO: this field missed in DTO, check it
          // @ts-ignore
          planId: toString(activity?.plan_id),
          planName: activity?.plan_name,
          taxType: activity?.tax_type as unknown as TaxType,
          taxYear: activity?.tax_year,
          employeeId: toString(activity?.employee_id),
          employeeFullName: activity?.employee_full_name,
          contributionDateOn: activity?.contribution_date_on as unknown as string,
          employeeContributionAmount: toString(activity?.ee_contribution_amount),
          employerContributionAmount: toString(activity?.er_contribution_amount),
          memo: activity?.memo,
          subsidiaryName: activity?.subsidiary_name,
          disabled: activity?.disabled,
          // TODO: it seems this field is not used
          // @ts-ignore
          isDeleted: activity?.disabled,
        })
      ),
    [data]
  );

  return {
    formattedData,
    totalPages: data?.data?.totalPages,
    totalElements: data?.data?.totalElements,
    // TODO: this field is missing
    // @ts-ignore
    numberOfElements: data?.data?.numberOfElements,
    isLoading,
    isEmpty: data?.data?.empty,
  };
};

export default useGetContributionActivitiesQuery;
